import React, { useState, useEffect } from 'react';
import {
    Row,
    Col,
    Card,
    Table,
    Alert,
    Button,
    Modal,
    Spinner
} from 'react-bootstrap';
import { FiTrash2 } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import PaginationComponent from '../common/Pagination';
import instance from "../../services/baseService";
import { PER_PAGE } from '../../constants/user'
const MissingRecordsTab = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [missingRecordsData, setMissingRecordsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [deleteItemSbNo, setDeleteItemSbNo] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // Get client_id from URL params and upload_id (file_id)
    const { client_id, report_id } = useParams();

    const transformApiData = (apiData) => {
        return apiData.map(item => ({
            id: item.id,
            sbNo: item.sbNo,
            sbDate: item.sbDate,
            portCode: item.portCode,
            rosctelAmount: item.rosctelAmount,
            rodTepAmount: item.rodTepAmount
        }));
    };

    // Data transformation functions
    const formatCurrency = (amount) => {
        if (!amount || amount === null || amount === undefined) return '₹0';
        const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
        return `₹${numAmount.toLocaleString('en-IN')}`;
    };

    const formatDate = (dateString) => {
        if (!dateString || dateString === null) return '-';
        try {
            return new Date(dateString).toLocaleDateString('en-IN');
        } catch (error) {
            return '-';
        }
    };

    const formatDisplayValue = (value, fallback = '-') => {
        if (value === null || value === undefined || value === '') return fallback;
        return value;
    };

    // API Functions
    const fetchMissingRecords = async (page = 1, showLoader = true) => {
        if (showLoader) {
            setLoading(true);
        }
        setError('');

        try {
            const res = await instance.post(
                `/api/v1/reconciliation/view/missing/report/${client_id}/${report_id}`,
                {
                    page: page,
                    perpage: PER_PAGE
                }
            );

            if (res.data && res.data.type === 'success') {
                const responseData = res.data.data;
                const transformedData = transformApiData(responseData.lists || []);
                setMissingRecordsData(transformedData);
                setTotalCount(responseData.total_count || 0);
                setCurrentPage(responseData.page || page);

                if ((responseData.lists?.length === 0 || !responseData.lists) && page === 1) {
                    setSuccess('No missing records found');
                }
            } else {
                throw new Error(res.data?.message || 'Failed to fetch missing records');
            }

        } catch (err) {
            console.error('Fetch Missing Records Error:', err);

            // Handle different error scenarios
            if (err.response) {
                const { status, data } = err.response;
                if (status === 404) {
                    setError('Missing records not found. Please check if the data exists.');
                } else if (status === 403) {
                    setError('Access denied. You don\'t have permission to view missing records.');
                } else if (status >= 500) {
                    setError('Server error occurred. Please try again later.');
                } else {
                    setError(data?.message || 'Failed to fetch missing records. Please try again.');
                }
            } else if (err.request) {
                setError('Network error. Please check your connection and try again.');
            } else {
                setError(err.message || 'Failed to fetch missing records. Please try again.');
            }

            setMissingRecordsData([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
            setInitialLoading(false);
        }
    };

    const deleteRecord = async (sbNo) => {
        setDeleteLoading(true);
        setError('');

        try {
            // Make delete API call with sb_nos array
            const res = await instance.post(
                `/api/v1/reconciliation/delete/missing/${client_id}/${report_id}`,
                {
                    sb_nos: [sbNo] // Send as array as required by API
                }
            );

            if (res.data && res.data.type === 'success') {
                // Remove the deleted item from the list
                setMissingRecordsData(prev => prev.filter(item => item.sbNo !== sbNo));
                setTotalCount(prev => prev - 1);

                // Show success message from API response
                toast.success(res.data.message || 'Record deleted successfully');

                // If current page becomes empty and it's not the first page, go to previous page
                const remainingRecords = missingRecordsData.filter(item => item.sbNo !== sbNo);
                if (remainingRecords.length === 0 && currentPage > 1) {
                    const newPage = currentPage - 1;
                    setCurrentPage(newPage);
                    fetchMissingRecords(newPage, false);
                }
            } else {
                throw new Error(res.data?.message || 'Failed to delete record');
            }

        } catch (err) {
            console.error('Delete Record Error:', err);

            // Handle different error scenarios
            let errorMessage = 'Failed to delete record. Please try again.';

            if (err.response) {
                const { status, data } = err.response;
                if (status === 404) {
                    errorMessage = 'Record not found or already deleted.';
                } else if (status === 403) {
                    errorMessage = 'Access denied. You don\'t have permission to delete this record.';
                } else if (status >= 500) {
                    errorMessage = 'Server error occurred. Please try again later.';
                } else {
                    errorMessage = data?.message || errorMessage;
                }
            } else if (err.request) {
                errorMessage = 'Network error. Please check your connection and try again.';
            } else {
                errorMessage = err.message || errorMessage;
            }

            toast.error(errorMessage);
            setError(errorMessage);
        } finally {
            setDeleteLoading(false);
            setShowDeleteModal(false);
            setDeleteItemId(null);
            setDeleteItemSbNo(null);
        }
    };

    // Event Handlers
    const handleDeleteClick = (id, sbNo) => {
        setDeleteItemId(id);
        setDeleteItemSbNo(sbNo);
        setShowDeleteModal(true);
    };

    const handleDelete = () => {
        if (deleteItemSbNo) {
            deleteRecord(deleteItemSbNo);
        }
    };

    const handlePagination = async (page) => {
        setCurrentPage(page);
        await fetchMissingRecords(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setDeleteItemId(null);
        setDeleteItemSbNo(null);
    };

    // Load initial data
    useEffect(() => {
        if (client_id && report_id) {
            fetchMissingRecords(1);
        } else {
            setError('Client ID and File ID are required to fetch missing records.');
            setInitialLoading(false);
        }
    }, [client_id, report_id]);

    // Auto-clear success message
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => setSuccess(''), 5000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    const clearMessages = () => {
        setError('');
        setSuccess('');
    };

    return (
        <div>
            {/* Header */}
            <Row className="align-items-center justify-content-between mb-4">
                <Col>
                    <h4 className="fw-bold mb-0">Missing Records</h4>
                    <p className="text-muted mb-0">Review and manage missing records.</p>
                </Col>
            </Row>

            {/* Success Alert */}
            {success && (
                <Alert variant="success" dismissible onClose={clearMessages} className="mb-3">
                    {success}
                </Alert>
            )}

            {/* Error Alert */}
            {error && (
                <Alert variant="danger" dismissible onClose={clearMessages} className="mb-3">
                    {error}
                </Alert>
            )}

            <Card className="border-0">
                <Card.Body>
                    {initialLoading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" role="status" className="mb-3">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                            <p className="text-muted">Loading missing records...</p>
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <Table hover className="core-ui-table">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="text-left" scope="col">SB No</th>
                                            <th className="text-left" scope="col">SB Date</th>
                                            <th className="text-left" scope="col">Port Code</th>
                                            <th className="text-left" scope="col">Rosctel Amount</th>
                                            <th className="text-left" scope="col">RodTep Amount</th>
                                            <th className="text-center" scope="col">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading && missingRecordsData.length > 0 && (
                                            <tr>
                                                <td colSpan="6" className="text-center py-2">
                                                    <small className="text-muted">
                                                        <Spinner animation="border" size="sm" className="me-2" />
                                                        Updating records...
                                                    </small>
                                                </td>
                                            </tr>
                                        )}

                                        {missingRecordsData.length > 0 ? (
                                            missingRecordsData.map((item) => (
                                                <tr key={item.id}>
                                                    <td className="text-left text-primary fw-medium">
                                                        {formatDisplayValue(item.sbNo)}
                                                    </td>
                                                    <td className="text-left">
                                                        {formatDate(item.sbDate)}
                                                    </td>
                                                    <td className="text-left text-primary fw-medium">
                                                        {formatDisplayValue(item.portCode)}
                                                    </td>
                                                    <td className="text-left">
                                                        {formatCurrency(item.rosctelAmount)}
                                                    </td>
                                                    <td className="text-left">
                                                        {formatCurrency(item.rodTepAmount)}
                                                    </td>
                                                    <td className="text-center">
                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            onClick={() => handleDeleteClick(item.id, item.sbNo)}
                                                            disabled={deleteLoading}
                                                            style={{ width: '32px', height: '32px' }}
                                                            title="Delete Record"
                                                        >
                                                            <FiTrash2 color='red' size={15} />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="text-center py-4 text-muted">
                                                    {error ? 'Unable to load records' : 'No missing records found'}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalCount > PER_PAGE && (
                                <div className="mt-3">
                                    <PaginationComponent
                                        total_count={totalCount}
                                        current_page={currentPage}
                                        per_page={PER_PAGE}
                                        handleOnClick={handlePagination}
                                    />
                                </div>
                            )}

                            {/* Data Summary */}
                            {!loading && missingRecordsData.length > 0 && (
                                <div className="mt-3 text-muted small">
                                    Showing {missingRecordsData.length} of {totalCount} records
                                </div>
                            )}
                        </>
                    )}

                    {/* Delete Confirmation Modal */}
                    <Modal show={showDeleteModal} onHide={closeDeleteModal} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>Confirm Delete</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            Are you sure you want to delete this missing record?
                            {deleteItemSbNo && (
                                <div className="mt-2">
                                    <strong>SB No:</strong> {deleteItemSbNo}
                                </div>
                            )}
                            <div className="mt-2 small text-muted">
                                This action cannot be undone.
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={closeDeleteModal}
                                disabled={deleteLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="danger"
                                onClick={handleDelete}
                                disabled={deleteLoading}
                                className="d-flex align-items-center"
                            >
                                {deleteLoading ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <FiTrash2 className="me-2" />
                                        Delete
                                    </>
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </Card.Body>
            </Card>
        </div>
    );
};

export default MissingRecordsTab;